import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Button, Col, Container, Form, Row, Spinner } from 'react-bootstrap';
import { ApiGet, ApiPost } from './Api';

const TEST_DOMAIN = 'raynertrust.co.uk';
const WORKFLOW_API_PATH = 'manage-workflow';
const WORKFLOW_STORAGE_KEY = 'manageWorkflowExecutionArn';

const HIGH_LEVEL_OPERATIONS = [
  {
    key: 'php-provision',
    title: 'Prepare hosting account',
    description: 'Configure the approved PHP version and extensions before WordPress is installed.',
    buttonLabel: 'Prepare PHP'
  },
  {
    key: 'wordpress-post-install',
    title: 'Configure WordPress',
    description: 'Install the approved plugins and theme, then configure Updraft after WordPress has been installed through Installatron.',
    buttonLabel: 'Configure WordPress'
  }
];

const PHASE_LABELS = {
  starting: 'Starting',
  enabling: 'Enabling',
  enabled: 'Enabled temporarily',
  waiting: 'Enabled temporarily',
  disabling: 'Disabling',
  complete: 'Inactive',
  failed: 'Workflow failed'
};

const MANAGED_SERVICES = [
  {
    key: 'elementor-manage',
    title: 'Elementor Manage',
    description: `Temporarily enable Elementor Manage on ${TEST_DOMAIN}.`,
    apiPath: 'plugin-window',
    scope: 'single',
    enableOperation: 'enable-window',
    disableOperation: 'disable',
    enableLabel: 'Enable for 30 minutes',
    disableLabel: 'Disable now',
    enableConfirmation: `Enable Elementor Manage on ${TEST_DOMAIN} for 30 minutes? It will be disabled automatically.`,
    disableConfirmation: `Disable Elementor Manage on ${TEST_DOMAIN} now?`
  }
];

const formatDate = value => {
  if (!value) {
    return null;
  }

  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? value : date.toLocaleString();
};

const getErrorMessage = error => {
  if (error?.response?.data?.error) {
    return error.response.data.error;
  }

  if (error?.message) {
    return error.message;
  }

  return String(error);
};

const normaliseDomain = value => String(value ?? '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '');

const isValidDomain = value => /^(?=.{1,253}$)(?:[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/i.test(value);

const ProvisioningOperations = () => {
  const [domain, setDomain] = useState('');
  const [pendingWorkflow, setPendingWorkflow] = useState(null);
  const [executionArn, setExecutionArn] = useState(() => window.localStorage.getItem(WORKFLOW_STORAGE_KEY));
  const [workflowStatus, setWorkflowStatus] = useState(null);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  const loadWorkflowStatus = useCallback(async arn => {
    try {
      const result = await ApiPost(WORKFLOW_API_PATH, { request: 'status', executionArn: arn });
      setWorkflowStatus(result);

      if (result?.domain) {
        setDomain(result.domain);
      }

      if (result?.running !== true) {
        window.localStorage.removeItem(WORKFLOW_STORAGE_KEY);
        setExecutionArn(null);
      }

      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    }
  }, []);

  useEffect(() => {
    if (!executionArn) {
      return undefined;
    }

    loadWorkflowStatus(executionArn);
    const timer = window.setInterval(() => loadWorkflowStatus(executionArn), 5000);
    return () => window.clearInterval(timer);
  }, [executionArn, loadWorkflowStatus]);

  const startWorkflow = async operation => {
    const targetDomain = normaliseDomain(domain);

    if (!isValidDomain(targetDomain)) {
      setMessage('');
      setError('Enter a valid domain name, for example example.co.uk.');
      return;
    }

    if (!window.confirm(`${operation.title} for ${targetDomain}?`)) {
      return;
    }

    setPendingWorkflow(operation.key);
    setMessage('');
    setError('');

    try {
      const result = await ApiPost(WORKFLOW_API_PATH, {
        domain: targetDomain,
        workflow: operation.key
      });

      if (!result?.executionArn) {
        throw new Error('The server started the workflow but did not return an execution ARN');
      }

      window.localStorage.setItem(WORKFLOW_STORAGE_KEY, result.executionArn);
      setDomain(targetDomain);
      setWorkflowStatus({ ...result, status: 'RUNNING', running: true });
      setExecutionArn(result.executionArn);
      setMessage('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setPendingWorkflow(null);
    }
  };

  return (
    <>
      <Row className="mt-4">
        <Col>
          <h2 className="text-center">Site provisioning</h2>
          <p className="text-center">Enter the site domain, then choose the operation to run.</p>
        </Col>
      </Row>

      <Row className="justify-content-center">
        <Col md={8} lg={6}>
          <Form.Group controlId="manage-domain">
            <Form.Label>Domain</Form.Label>
            <Form.Control
              type="text"
              value={domain}
              disabled={pendingWorkflow !== null}
              placeholder="domain name"
              autoComplete="off"
              onChange={event => setDomain(event.target.value)}
              onKeyDown={event => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                }
              }}
            />
          </Form.Group>

          {error && <Alert variant="danger" className="mt-3">{error}</Alert>}
          {message && <Alert variant="info" className="mt-3">{message}</Alert>}

          {workflowStatus?.status === 'RUNNING' && (
            <Alert variant="info" className="mt-3">
              <strong>Workflow running.</strong>
              <div>{workflowStatus.workflow} is running for {workflowStatus.domain}.</div>
              {workflowStatus.startedAt && <div>Started: {formatDate(workflowStatus.startedAt)}</div>}
            </Alert>
          )}

          {workflowStatus?.status === 'SUCCEEDED' && (
            <Alert variant="success" className="mt-3">
              <strong>Workflow completed successfully.</strong>
              <div>{workflowStatus.workflow} completed for {workflowStatus.domain}.</div>
              {workflowStatus.stoppedAt && <div>Finished: {formatDate(workflowStatus.stoppedAt)}</div>}
            </Alert>
          )}

          {workflowStatus && !['RUNNING', 'SUCCEEDED'].includes(workflowStatus.status) && (
            <Alert variant="danger" className="mt-3">
              <strong>Workflow failed.</strong>
              <div>{workflowStatus.workflow} did not complete for {workflowStatus.domain}.</div>
              {workflowStatus.error && <div>Error: {workflowStatus.error}</div>}
              {workflowStatus.cause && <div>{workflowStatus.cause}</div>}
              {workflowStatus.stoppedAt && <div>Stopped: {formatDate(workflowStatus.stoppedAt)}</div>}
            </Alert>
          )}
        </Col>
      </Row>

      <Row className="justify-content-center">
        {HIGH_LEVEL_OPERATIONS.map(operation => (
          <Col md={6} lg={5} className="mt-3" key={operation.key}>
            <div className="border rounded p-3 h-100 text-center">
              <h3>{operation.title}</h3>
              <p>{operation.description}</p>
              <Button variant="primary" disabled={pendingWorkflow !== null || executionArn !== null} onClick={() => startWorkflow(operation)}>
                {pendingWorkflow === operation.key ? <><Spinner animation="border" size="sm" /> Starting…</> : operation.buttonLabel}
              </Button>
            </div>
          </Col>
        ))}
      </Row>
    </>
  );
};

const ManagedService = ({ service }) => {
  const [loading, setLoading] = useState(true);
  const [pendingOperation, setPendingOperation] = useState(null);
  const [running, setRunning] = useState(false);
  const [phase, setPhase] = useState(null);
  const [startedAt, setStartedAt] = useState(null);
  const [disableAt, setDisableAt] = useState(null);
  const [domain, setDomain] = useState(TEST_DOMAIN);
  const [error, setError] = useState('');

  const loadStatus = useCallback(async () => {
    try {
      const result = await ApiGet(service.apiPath);
      setRunning(result?.running === true);
      setPhase(result?.phase ?? null);
      setStartedAt(result?.startedAt ?? null);
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.domain ?? TEST_DOMAIN);
      setError('');
    } catch (requestError) {
      setError(getErrorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, [service.apiPath]);

  useEffect(() => {
    loadStatus();
  }, [loadStatus]);

  useEffect(() => {
    const interval = running && phase !== 'waiting' ? 5000 : 30000;
    const timer = window.setInterval(loadStatus, interval);
    return () => window.clearInterval(timer);
  }, [running, phase, loadStatus]);

  const startOperation = async operation => {
    setPendingOperation(operation);
    setError('');

    try {
      const result = await ApiPost(service.apiPath, { operation, scope: service.scope });
      setRunning(true);
      setPhase(operation === service.enableOperation ? 'starting' : 'disabling');
      setStartedAt(result?.startedAt ?? new Date().toISOString());
      setDisableAt(result?.disableAt ?? null);
      setDomain(result?.domain ?? TEST_DOMAIN);
    } catch (requestError) {
      setError(getErrorMessage(requestError));
      await loadStatus();
    } finally {
      setPendingOperation(null);
    }
  };

  const disableBlocked = loading || pendingOperation !== null || (running && ['starting', 'enabling', 'disabling'].includes(phase));

  const renderStatus = () => {
    if (loading) {
      return <div className="text-center"><Spinner animation="border" /></div>;
    }

    if (!running) {
      return <Alert variant="secondary"><strong>{service.title} is inactive.</strong><div>No temporary-access workflow is currently running.</div><div>Target domain: {domain}</div></Alert>;
    }

    return (
      <Alert variant={phase === 'failed' ? 'danger' : 'warning'}>
        <div><strong>{service.title}: {PHASE_LABELS[phase] ?? 'Workflow running'}</strong></div>
        <div>Target domain: {domain}</div>
        {startedAt && <div>Started: {formatDate(startedAt)}</div>}
        {disableAt && <div>Automatic disable: {formatDate(disableAt)}</div>}
        {!disableAt && phase !== 'disabling' && <div>Automatic disable is scheduled by the workflow.</div>}
      </Alert>
    );
  };

  return (
    <>
      <Row className="mt-5">
        <Col><h2 className="text-center">{service.title}</h2><p className="text-center">{service.description}</p></Col>
      </Row>
      <Row><Col>{error && <Alert variant="danger"><strong>Unable to complete the request.</strong><div>{error}</div></Alert>}{renderStatus()}</Col></Row>
      <Row>
        <Col>
          <div className="text-center">
            <Button variant="primary" disabled={loading || pendingOperation !== null || running} onClick={() => window.confirm(service.enableConfirmation) && startOperation(service.enableOperation)}>
              {pendingOperation === service.enableOperation ? 'Enabling…' : service.enableLabel}
            </Button>{' '}
            <Button variant="danger" disabled={disableBlocked} onClick={() => window.confirm(service.disableConfirmation) && startOperation(service.disableOperation)}>
              {pendingOperation === service.disableOperation ? 'Disabling…' : service.disableLabel}
            </Button>{' '}
            <Button variant="outline-secondary" disabled={loading || pendingOperation !== null} onClick={loadStatus}>Refresh status</Button>
          </div>
        </Col>
      </Row>
    </>
  );
};

const Manage = () => (
  <Container fluid="lg">
    <Row><Col><h1 className="text-center">Manage</h1></Col></Row>
    <ProvisioningOperations />
    {MANAGED_SERVICES.map(service => <ManagedService key={service.key} service={service} />)}
  </Container>
);

export { Manage };
